import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "react-qr-code";
import html2canvas from "html2canvas";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

// ── Dark teal design tokens (match Profile.js / Dashboard.js) ──
const T = {
  bg:          "#060d1f",
  bgCard:      "rgba(10, 25, 50, 0.82)",
  teal:        "#00c9b1",
  tealDim:     "rgba(0, 201, 177, 0.18)",
  tealBorder:  "rgba(0, 201, 177, 0.28)",
  text:        "#e0f7f4",
  textDim:     "rgba(160, 210, 220, 0.65)",
  header:      "linear-gradient(135deg, #0a2540 0%, #0d3060 100%)",
  font:        "'Poppins', 'Segoe UI', sans-serif",
};

export default function VendorIdCard() {
  const navigate = useNavigate();
  const cardRef  = useRef();

  const [vendorData, setVendorData] = useState(null);

  // ✅ ROBUST LOAD: localStorage first, then Firestore
  useEffect(() => {
    try {
      const cached = JSON.parse(localStorage.getItem("vendorData"));
      if (cached && (cached.name || cached.id)) {
        setVendorData(cached);
      }
    } catch (e) {}

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) { navigate("/", { replace: true }); return; }
      try {
        const snap = await getDoc(doc(db, "vendors", user.uid));
        if (snap.exists()) {
          const data = { ...snap.data(), uid: user.uid };
          setVendorData(data);
          localStorage.setItem("vendorData", JSON.stringify(data));
        } else {
          navigate("/", { replace: true });
        }
      } catch (err) {
        console.error("Vendor fetch error:", err);
      }
    });

    return () => unsub();
  }, [navigate]);

  const downloadCard = async () => {
    try {
      if (!cardRef.current) return;
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 2
      });
      const link = document.createElement("a");
      link.download = "Vendor_ID_Card.png";
      link.href = canvas.toDataURL();
      link.click();
    } catch (err) {
      console.error("Download error:", err);
    }
  };

  if (!vendorData) {
    return (
      <div style={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: T.bg,
        color: T.text,
        fontFamily: T.font
      }}>
        <h2>Loading Vendor Card...</h2>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      background: "radial-gradient(ellipse at top, #0a1628 0%, #060d1f 70%)",
      padding: 20,
      fontFamily: T.font
    }}>
      <div style={{ width: "100%", maxWidth: 340 }}>

        {/* ── CARD ─────────────────────────────────────── */}
        <div
          ref={cardRef}
          style={{
            padding: "28px 22px",
            borderRadius: 24,
            background: T.bgCard,
            border: `1px solid ${T.tealBorder}`,
            boxShadow: "0 20px 60px rgba(0,0,0,0.6), 0 0 30px rgba(0, 201, 177, 0.08)",
            textAlign: "center",
            color: T.text,
            backdropFilter: "blur(12px)",
          }}
        >
          <h2 style={{
            margin: "0 0 4px 0",
            fontSize: 24,
            fontWeight: 800,
            background: "linear-gradient(135deg, #00c9b1, #0077cc)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            letterSpacing: 0.5
          }}>
            Connect
          </h2>
          <p style={{ color: T.textDim, fontSize: 13, margin: "4px 0 14px" }}>
            Vendor Identification Card
          </p>

          {/* PROFILE IMAGE (always renders something) */}
          <Avatar
            src={vendorData.imageUrl || vendorData.image || vendorData.photoURL}
            label={vendorData.name || vendorData.shopName || "V"}
            size={92}
          />

          {/* DETAILS */}
          <div style={{ textAlign: "left", margin: "10px 6px 14px" }}>
            <DetailRow label="ID"          value={vendorData.id          || "N/A"} />
            <DetailRow label="Name"        value={vendorData.name        || "Vendor"} />
            <DetailRow label="Shop"        value={vendorData.shopName    || "N/A"} />
            <DetailRow label="Phone"       value={vendorData.phone       || "N/A"} />
            <DetailRow label="Category"    value={vendorData.category    || "N/A"} />
            <DetailRow label="Subcategory" value={vendorData.subcategory || "N/A"} />
            <DetailRow label="Sub District" value={vendorData.subdistrict || "N/A"} />
          </div>

          {/* QR */}
          <div style={{
            display: "inline-block",
            background: "#fff",
            padding: 8,
            borderRadius: 10,
            marginTop: 6,
          }}>
            <QRCode value={vendorData.id || vendorData.uid || "VENDOR"} size={90} />
          </div>

          {/* VERIFIED BADGE */}
          <div style={{
            marginTop: 16,
            padding: "8px 14px",
            background: "linear-gradient(135deg, #00c9b1, #0077cc)",
            color: "#fff",
            borderRadius: 20,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: 0.3,
            display: "inline-block",
            boxShadow: "0 4px 16px rgba(0,201,177,0.35)"
          }}>
            ✔ Verified Vendor
          </div>
        </div>

        {/* ── BUTTONS ─────────────────────────────────── */}
        <div style={{
          marginTop: 18,
          display: "flex",
          gap: 10,
          justifyContent: "center",
        }}>
          <button onClick={downloadCard} style={btnPrimary}>
            ⬇ Download
          </button>
          <button
            onClick={() => navigate("/vendor-dashboard")}
            style={btnGhost}
          >
            ← Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Avatar that always renders something — never a broken image ─
function Avatar({ src, label, size = 92 }) {
  const [broken, setBroken] = useState(!src);
  const initial = (label || "V").trim().charAt(0).toUpperCase();
  if (broken || !src) {
    return (
      <div style={{
        width: size, height: size, borderRadius: "50%",
        background: "linear-gradient(135deg, #00c9b1, #0077cc)",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        color: "#fff", fontWeight: 800,
        fontSize: Math.round(size * 0.42),
        border: `3px solid #00c9b1`,
        boxShadow: "0 0 18px rgba(0, 201, 177, 0.45)",
        marginBottom: 14,
      }}>{initial}</div>
    );
  }
  return (
    <img
      src={src}
      alt="profile"
      onError={() => setBroken(true)}
      style={{
        width: size, height: size, borderRadius: "50%",
        objectFit: "cover", border: `3px solid #00c9b1`,
        boxShadow: "0 0 18px rgba(0, 201, 177, 0.45)",
        marginBottom: 14,
      }}
    />
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={{
      display: "flex",
      justifyContent: "space-between",
      padding: "6px 0",
      borderBottom: "1px solid rgba(0, 201, 177, 0.1)",
      fontSize: 13,
    }}>
      <span style={{ color: "rgba(160, 210, 220, 0.75)", fontWeight: 600 }}>
        {label}
      </span>
      <span style={{
        color: "#e0f7f4",
        maxWidth: "60%",
        textAlign: "right",
        wordBreak: "break-word",
      }}>
        {value}
      </span>
    </div>
  );
}

const btnPrimary = {
  padding: "11px 22px",
  borderRadius: 25,
  border: "none",
  background: "linear-gradient(135deg, #00c9b1, #0077cc)",
  color: "#fff",
  fontWeight: 700,
  fontSize: 14,
  cursor: "pointer",
  boxShadow: "0 6px 20px rgba(0, 201, 177, 0.35)",
  fontFamily: "'Poppins', 'Segoe UI', sans-serif",
};

const btnGhost = {
  padding: "11px 22px",
  borderRadius: 25,
  border: "1px solid rgba(0, 201, 177, 0.28)",
  background: "rgba(0, 201, 177, 0.08)",
  color: "#00c9b1",
  fontWeight: 700,
  fontSize: 14,
  cursor: "pointer",
  fontFamily: "'Poppins', 'Segoe UI', sans-serif",
};